<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import HotKeyCard from './HotKeyCard.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import FormSection from 'dashboard/components/FormSection.vue';
import AccessToken from './AccessToken.vue';
import Policy from 'dashboard/components/policy.vue';
import ThemeSetting from './ThemeSetting.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import FormButton from 'v3/components/Form/Button.vue';
import NotificationLite from './NotificationLite.vue';
import SectionLayout from 'dashboard/components-next/SectionLayout.vue';

export default {
  components: {
    MessageSignature,
    FormSection,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    HotKeyCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    NotificationLite,
    AccessToken,
    ThemeSetting,
    FormButton,
    SectionLayout,
  },
  mixins: [globalConfigMixin],
  setup() {
    const { uiSettings, updateUISettings, isEditorHotKeyEnabled } =
      useUISettings();

    return {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
      referenceCode: '',
      referenceCodeInput: '',
      referenceCodeLocked: false,
      referenceCodeError: '',
      isUpdatingReferenceCode: false,
      referrerCodeUsed: '',
      showAdvancedSettings: true,
      showNotificationLite: false,
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
    }),
  },
  watch: {
    'currentUser.reference_code'(value) {
      this.referenceCode = value || '';
      if (!this.isUpdatingReferenceCode) {
        this.referenceCodeInput = this.referenceCode;
      }
    },
    'currentUser.reference_code_locked'(value) {
      this.referenceCodeLocked = Boolean(value);
    },
    'currentUser.referrer_code_used'(value) {
      this.referrerCodeUsed = value || '';
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
      this.referenceCode = this.currentUser.reference_code || '';
      this.referenceCodeLocked = this.currentUser.reference_code_locked || false;
      this.referenceCodeInput = this.referenceCode;
      this.referenceCodeError = '';
      this.referrerCodeUsed = this.currentUser.referrer_code_used || '';
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      let isSuccess = false;
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;
        isSuccess = true;
        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;
        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage, { type: isSuccess ? 'success' : 'error' });
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const hasEmailChanged = email !== undefined && this.currentUser.email !== email;
      // Preserve existing values only when the field is truly undefined.
      this.name = name !== undefined ? name : this.name;
      this.email = email !== undefined ? email : this.email;
      this.displayName = displayName !== undefined ? displayName : this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName, // allow empty string to clear display name
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateReferenceCode() {
      if (this.referenceCodeLocked) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.LOCKED'), {type: 'info'});
        return;
      }

      const nextCode = (this.referenceCodeInput || '').replace(/\s+/g, '').toUpperCase();

      if (!nextCode) {
        this.referenceCodeError = this.$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.REQUIRED_ERROR');
        return;
      }
      if (!/^[A-Z0-9]{4,16}$/.test(nextCode)) {
        this.referenceCodeError = this.$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.FORMAT_ERROR');
        return;
      }
      if (nextCode === this.referenceCode) {
        return;
      }

      this.referenceCodeError = '';
      this.referenceCodeInput = nextCode;
      this.isUpdatingReferenceCode = true;

      const success = await this.dispatchUpdate(
        { reference_code: nextCode },
        this.$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.SUCCESS'),
        this.$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.SERVER_ERROR')
      );

      this.isUpdatingReferenceCode = false;

      if (!success) {
        return;
      }

      this.referenceCode = nextCode;
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'), {type: 'success'});
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'), {type: 'error'});
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'), {type: 'success'});
    },
    async onCopyToken(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'), {
        type: 'success',
      });
    },
  },
};
</script>
  <template>
    <SectionLayout
      :title="$t('PROFILE_SETTINGS.TITLE')"
      :show-back-button="true"
      :has-menu-button="false"
      class="overflow-y-auto"
    >
      <div class="flex flex-col py-4 gap-12 w-full lg:flex-row lg:gap-6 lg:gap-12 lg:px-[120px] lg:py-6">
        <!-- <div class="flex flex-col gap-6 items-center lg:items-start">
          <UserProfilePicture
            :src="avatarUrl"
            :name="name"
            @change="updateProfilePicture"
            @delete="deleteProfilePicture"
          />
        </div> -->

        <div class="flex flex-col gap-3 lg:gap-6 flex-1 mt-6 md:mt-0">
          <FormSection>
            <template #leftSection>
              <UserProfilePicture
                :src="avatarUrl"
                :name="name"
                @change="updateProfilePicture"
                @delete="deleteProfilePicture"
              />
            </template>
            <template #actions>
              <button
                class="h-10 input-base bg-green-500 text-white text-xs"
                @click="$refs.userDetails.updateUser()"
              >
                {{ $t('PROFILE_SETTINGS.BTN_TEXT') }}
              </button>
            </template>
            <UserBasicDetails
              ref="userDetails"
              :name="name"
              :display-name="displayName"
              :email="email"
              :email-enabled="!globalConfig.disableUserProfileUpdate"
              @update-user="updateProfile"
            />
          </FormSection>

          <FormSection
            :title="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.TITLE')"
            :description="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.NOTE')"
          >
            <div class="flex flex-col gap-3 md:flex-row md:items-end">
              <woot-input
                v-model="referenceCodeInput"
                :styles="darkInputStyles"
                :disabled="referenceCodeLocked || isUpdatingReferenceCode"
                maxlength="16"
                autocomplete="off"
                :label="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.LABEL')"
                :placeholder="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.PLACEHOLDER')"
                :error="referenceCodeError"
              />
              <FormButton
                v-if="!referenceCodeLocked"
                type="button"
                color-scheme="primary"
                variant="solid"
                size="large"
                :disabled="isUpdatingReferenceCode"
                @click="updateReferenceCode"
              >
                {{ $t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.BTN_TEXT') }}
              </FormButton>
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-300 mt-1">
              {{
                referenceCodeLocked
                  ? $t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.LOCKED')
                  : $t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.HELP')
              }}
            </p>
            <div class="flex flex-col gap-2 mt-4">
              <woot-input
                v-model="referrerCodeUsed"
                :styles="darkInputStyles"
                disabled
                :label="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.REFERRER_LABEL')"
                :placeholder="$t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.REFERRER_PLACEHOLDER')"
              />
              <p class="text-xs text-slate-500 dark:text-slate-300">
                {{ $t('PROFILE_SETTINGS.FORM.REFERENCE_CODE_SECTION.REFERRER_HELP') }}
              </p>
            </div>
          </FormSection>

          <FormSection :title="$t('PROFILE_SETTINGS.FORM.APPEARANCE_SECTION.TITLE')">
            <ThemeSetting />
          </FormSection>

          <FormSection v-if="!globalConfig.disableUserProfileUpdate" :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')">
            <template #actions>
              <button
                class="h-10 input-base bg-green-500 text-white text-xs"
                @click="$refs.changePasswordForm.changePassword()"
              >
                {{ $t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT') }}
              </button>
            </template>
            <ChangePassword ref="changePasswordForm" :show-submit="false" />
          </FormSection>

          <Policy :permissions="notificationPermissions" v-if="showNotificationLite">
            <FormSection :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS_SECTION.TITLE')">
              <NotificationLite />
            </FormSection>
          </Policy>
          <div
          v-if="showAdvancedSettings"
          class="flex flex-col gap-6"
          >
            <FormSection
            :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
            :description="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')"
          >
            <MessageSignature
              :message-signature="messageSignature"
              @update-signature="updateSignature"
            />
          </FormSection>
          <FormSection
            :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
            :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
          >
            <div
              class="flex flex-col justify-center w-full gap-6 sm:gap-4 sm:flex-row"
            >
              <button
                v-for="hotKey in hotKeys"
                :key="hotKey.key"
                class="px-0 reset-base"
              >
                <HotKeyCard
                  :key="hotKey.title"
                  :title="hotKey.title"
                  :description="hotKey.description"
                  :light-image="hotKey.lightImage"
                  :dark-image="hotKey.darkImage"
                  :active="isEditorHotKeyEnabled(hotKey.key)"
                  @click="toggleHotKey(hotKey.key)"
                />
              </button>
            </div>
          </FormSection>
          <!-- <FormSection
            v-if="!globalConfig.disableUserProfileUpdate"
            :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
          >
            <ChangePassword />
          </FormSection> -->
          <Policy :permissions="audioNotificationPermissions">
            <FormSection
              :title="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')"
              :description="
                $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
              "
            >
              <AudioNotifications />
            </FormSection>
          </Policy>
          <Policy :permissions="notificationPermissions">
            <FormSection :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')">
              <NotificationPreferences />
            </FormSection>
          </Policy>
          <FormSection
            :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
            :description="
              useInstallationName(
                $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE'),
                globalConfig.installationName
              )
            "
          >
            <AccessToken :value="currentUser.access_token" @on-copy="onCopyToken" />
          </FormSection>
          </div>
        </div>
      </div>
    </SectionLayout>
  </template>
